import React, { useCallback } from "react";
import { useClientId } from "common/SessionContext";
import { routes } from "utilities/routing/generatedRoutes";
import { AfterCreateAction } from "../types";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenProductionSidePanel } from "./ProductionSidePanel";

interface NewProductionFormState {
  title: string;
  status: server.dto.StatusSmall;
}

interface Props {
  onCreated: (production: server.dto.TourSmall) => void;
  statuses: server.dto.StatusSmall[];
  afterCreate?: AfterCreateAction;
}

const NewProductionSidePanel = (props: Props) => {
  const openProductionDetailSidePanel = useOpenProductionSidePanel();
  const clientId = useClientId();
  const afterCreate = props.afterCreate ?? "displayItemInSidebar";
  const [formState, setFormState] = React.useState<NewProductionFormState>({
    title: "",
    status: props.statuses[0],
  });

  const onChange = (newData: Partial<NewProductionFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const submit = () => {
    return window.CoreApi.Production.Create({
      name: formState.title,
      statusId: formState.status?.statusId,
    })
  }

  const onSuccess = async (productionId: number) => {
    if (props.onCreated) {
      const result = await window.CoreApi.Production.GetSidePanelData(productionId);
      if (result.data.hasErrors) {
        return;
      }
      props.onCreated({
        ...result.data.data.tour,
        artists: result.data.data.tour.artists.map(artist => artist.name),
      });
    } else {
      window.location.href = routes.production_Detail(productionId).absolute;
    }
  }

  return <SidePanelNewSectionTemplate
    onSave={(id) => {
      onSuccess(id);
      if (afterCreate === "displayItemInSidebar") {
        openProductionDetailSidePanel(id);
      }
    }}
    afterCreate={afterCreate}
    title={site.currentEnvironment.tour.newLabel}
    id="new-production-side-panel"
    trigger={undefined}
    submit={submit}
    sections={[{
      key: "new-production-side-panel",
      type: "regular",
      hideTitle: true,
      title: site.currentEnvironment.tour.newLabel,
      fields: [
        {
          key: "title",
          label: RESX.GeneralLabels.Name,
          render: () => (
            <RequiredTextInput
              focusOnMount
              maxLength={serverReference.constants.tour.maxNameLength}
              value={formState.title}
              validatorKey="title"
              onChange={title => onChange({ title })}
            />
          )
        },
        {
          key: "status",
          label: RESX.Status.resxStatus,
          render: () => (
            <StatusDropdown
              value={formState.status}
              onChange={status => onChange({ status })}
              mainArea="tour"
              clientId={clientId}
              options={undefined}
            />
          )
        }
      ]
    }]}
  />
}

export const useOpenNewProductionSidePanel = () => {
  const openSidePanel = useOpenSidePanel();
  const clientId = useClientId();

  const openNewProductionSidePanel = useCallback(async (props: Omit<Props, "statuses">) => {
    const res = await window.CoreApi.Status.ListSmall({ clientId, mainArea: "tour" });
    openSidePanel(
      <NewProductionSidePanel {...props} statuses={res.data.data} />,
      "new-production-side-panel",
      "large"
    );
  }, [openSidePanel, clientId]);

  return openNewProductionSidePanel;
}